import { useCallback, useMemo } from "react";
import { TaskStep } from "lib/task-steps";

interface UseStepNavigationOptions {
  steps: TaskStep[];
  currentStepNumber: number;
  isStepCompleted: (stepNumber: number) => boolean;
  saveCurrentStepDraft: () => Promise<void>;
  navigateToStep: (step: TaskStep) => void;
  onSaveError: (error: unknown) => void;
}

export function useStepNavigation({
  steps,
  currentStepNumber,
  isStepCompleted,
  saveCurrentStepDraft,
  navigateToStep,
  onSaveError,
}: UseStepNavigationOptions) {
  const currentStepIndex = steps.findIndex(
    (step) => step.stepNumber === currentStepNumber
  );
  const previousStep =
    currentStepIndex > 0 ? steps[currentStepIndex - 1] : null;
  const nextIncompleteStep =
    steps.find(
      (step) =>
        step.stepNumber !== currentStepNumber &&
        !isStepCompleted(step.stepNumber)
    ) || null;

  const completedSteps = useMemo(
    () => steps.filter((step) => isStepCompleted(step.stepNumber)).length,
    [isStepCompleted, steps]
  );
  const completionPercentage = steps.length
    ? Math.round((completedSteps / steps.length) * 100)
    : 0;

  const selectStep = useCallback(
    async (selectedStep: TaskStep) => {
      if (selectedStep.stepNumber === currentStepNumber) return;

      try {
        await saveCurrentStepDraft();
      } catch (error) {
        onSaveError(error);
        return;
      }

      navigateToStep(selectedStep);
    },
    [currentStepNumber, navigateToStep, onSaveError, saveCurrentStepDraft]
  );

  const selectPreviousStep = useCallback(async () => {
    if (!previousStep) return;

    try {
      await saveCurrentStepDraft();
    } catch (error) {
      onSaveError(error);
      return;
    }

    navigateToStep(previousStep);
  }, [navigateToStep, onSaveError, previousStep, saveCurrentStepDraft]);

  return {
    completedSteps,
    completionPercentage,
    nextIncompleteStep,
    previousStep,
    selectPreviousStep,
    selectStep,
  };
}
